import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    FaArrowLeft, FaMagic, FaCheck, FaTrash, FaUpload, FaUsers, FaImage,
    FaPlus, FaArrowUp, FaArrowDown, FaPaperclip, FaSave, FaFileUpload, FaVideo, FaPen,
    FaChevronDown, FaChevronRight, FaFilePdf, FaFileWord, FaFileAlt, FaRegCircle,
    FaCheckCircle, FaListUl, FaCog, FaQuestionCircle, FaTimes, FaLayerGroup,
    FaUserGraduate, FaArrowRight, FaExclamationTriangle,
} from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { useTeacherTaxonomy } from "../../hooks/useTeacherTaxonomy";
import { useConfirm } from "../../components/useConfirm";
import { MathText, MathJaxContext, mathJaxConfig } from "../../components/MathText";
import { formatAiText } from "../../utils/formatAiText";
import {
    ContentThumb, TopicSelect, SubjectPicker, ClassPills, TypePicker, ProfileSetupNotice,
} from "../../components/marketplace";
import { SecureMedia } from "../../components/protected";
import {
    EMPTY_QUESTION, QuestionFields, QuestionEditor, validateQuestion,
} from "../../components/QuestionForm";

// The lesson builder. A lesson is a handful of SUB-TOPICS (stored as
// `lesson_segments` — the API/DB name stays `segments` so the mobile app keeps
// working; "sub-topic" is what teachers see), each with text and/or one
// uploaded file, plus the practice questions generated from them.
//
// Laid out as four tabs rather than one long page: teachers were scrolling past
// everything to reach the part they wanted, and each sub-topic's editor was
// always fully expanded, so a 6-sub-topic lesson was a very long page. Now each
// tab is one screenful and sub-topic rows collapse to a single line.

const QTYPE_LABELS = { mcq: "Multiple choice", theory: "Theory" };

// A teacher can upload whole lessons as files — each one becomes its own
// sub-topic. What may be uploaded depends on the lesson's content type, and the
// two sets don't overlap: a video lesson takes videos, every other type takes
// documents (plus images on the per-sub-topic "Attach file" control).
// Deliberately no .doc/.ppt: the reader converts documents server-side and the
// pre-2007 binary formats can't be converted without an office suite, so a
// student would only ever see "this document can't be opened". The API rejects
// them too (middlewares/marketplaceUpload.js) with the same advice.
const VIDEO_ACCEPT = ".mp4,.mov,.webm,.m4v";
const DOC_ACCEPT = ".pdf,.docx,.pptx,.xlsx";
const BULK_ACCEPT = DOC_ACCEPT;
const SEGMENT_ACCEPT = `${DOC_ACCEPT},.jpg,.jpeg,.png`;

// The content type has to mean the same thing in both directions — see
// rejectMismatchedAttachment in LessonController, which enforces it server-side.
// Offering PDFs on a video lesson was a promise the rest of the app then broke:
// the upload succeeded, but AI generation is off for the whole lesson when its
// type is video, so the readable document it had just accepted was never read.
// Offering videos on a Lesson Notes lesson broke it from the other side — the
// card stayed badged "Lesson Notes" while opening a clip, and the AI skipped
// that sub-topic because it can't watch one.
const acceptFor = (isVideoLesson, base) => (isVideoLesson ? VIDEO_ACCEPT : base);

// Videos are held to a 50MB cap (enforced again server-side). We check on the
// client too so a teacher gets an instant, friendly rejection instead of waiting
// for a large upload to fail. Matches VIDEO_EXTENSIONS in marketplaceUpload.js.
const VIDEO_MAX_BYTES = 50 * 1024 * 1024;
const isVideoFile = (file) => !!file && /\.(mp4|mov|webm|m4v)$/i.test(file.name);

// Gate a chosen sub-topic file through the video size cap and, on a video
// lesson, the video-only rule. The `accept` attribute is only a filter in the
// file dialog — "All files" and drag-and-drop walk straight past it — so the
// same check has to run here. Returns the file when it's acceptable, or null
// (after toasting) so callers can reset the input.
const acceptSegmentFile = (file, isVideoLesson = false) => {
    if (!file) return null;
    if (isVideoLesson && !isVideoFile(file)) {
        toast.error("This is a video lesson — its sub-topics must be video files. Change the content type on Details to add notes or documents.");
        return null;
    }
    if (!isVideoLesson && isVideoFile(file)) {
        toast.error("Videos can only go in a Video lesson. Change the content type on Details to Video, or upload a PDF or Word file here.");
        return null;
    }
    if (isVideoFile(file) && file.size > VIDEO_MAX_BYTES) {
        toast.error(`That video is ${Math.round(file.size / (1024 * 1024))}MB — videos must be 50MB or smaller.`);
        return null;
    }
    return file;
};

// Icon + label for the file attached to a sub-topic, so the collapsed row still
// says what's inside it.
const ATTACHMENT_META = {
    pdf: { Icon: FaFilePdf, label: "PDF", chip: "bg-red-50 text-red-600" },
    docx: { Icon: FaFileWord, label: "Word", chip: "bg-blue-50 text-blue-600" },
    office: { Icon: FaFileAlt, label: "Document", chip: "bg-blue-50 text-blue-600" },
    video: { Icon: FaVideo, label: "Video", chip: "bg-rose-50 text-rose-600" },
    image: { Icon: FaImage, label: "Image", chip: "bg-emerald-50 text-emerald-600" },
};
const attachmentMeta = (kind) =>
    ATTACHMENT_META[kind] || { Icon: FaFileAlt, label: "File", chip: "bg-gray-100 text-gray-600" };

// Only these can be read by the AI. A video/image sub-topic needs its questions
// written by hand, and the UI says so up front rather than after a failed run.
const AI_READABLE_KINDS = new Set(["pdf", "docx", "office"]);
const isAiReadable = (seg) =>
    !!(seg.body && seg.body.trim()) || (seg.hasAttachment && AI_READABLE_KINDS.has(seg.attachment_kind));

const TABS = [
    { id: "details", label: "Details", Icon: FaCog },
    { id: "subtopics", label: "Sub-topics", Icon: FaListUl },
    { id: "questions", label: "Questions", Icon: FaQuestionCircle },
    { id: "students", label: "Students", Icon: FaUsers },
];
const TAB_IDS = TABS.map((t) => t.id);

const ContentBuilder = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // The open tab lives in the URL so it survives a reload and can be linked to
    // — which is how "create a lesson" drops the teacher straight on Sub-topics
    // instead of the Details form they just filled in. Replace rather than push,
    // so flipping tabs doesn't turn Back into a walk through them.
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = searchParams.get("tab");
    const tab = TAB_IDS.includes(urlTab) ? urlTab : "details";
    const setTab = (next) => setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", next);
        return params;
    }, { replace: true });
    const [lesson, setLesson] = useState(null);
    const [segments, setSegments] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [students, setStudents] = useState(null);
    // Counts are PER SUB-TOPIC — the API generates one batch per sub-topic.
    const [genOptions, setGenOptions] = useState({ mcqCount: 5, theoryCount: 2, flashcardCount: 4 });

    // Subject/class options come from the teacher's own profile; topics stay
    // global-per-subject (shared across classes).
    const { subjects, classes, topicsBySubject, hasSubjects, hasClasses, needsSetup } = useTeacherTaxonomy();
    const { confirm, ConfirmDialog } = useConfirm();

    // Which pending questions are checked to keep. After a generation we pre-check
    // everything the AI returned; the teacher unticks what they don't want.
    const [selectedQ, setSelectedQ] = useState(() => new Set());
    const [reviewBusy, setReviewBusy] = useState(false);
    // The question currently open in the inline editor (draft or approved).
    const [editingQId, setEditingQId] = useState(null);

    const [settings, setSettings] = useState(null);
    const [cover, setCover] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [uploadingDocs, setUploadingDocs] = useState(false);
    // Several sub-topics can be open together. Only one could be before, so
    // comparing two of them — or working down a list — meant the one you'd just
    // read collapsed the moment you opened the next.
    const [openSegments, setOpenSegments] = useState(() => new Set());
    const toggleSegmentOpen = (sid) => setOpenSegments((prev) => {
        const next = new Set(prev);
        if (next.has(sid)) next.delete(sid); else next.add(sid);
        return next;
    });
    const [addingSegment, setAddingSegment] = useState(false);
    // Which sub-topic the Questions tab is showing. "all" is the whole lesson.
    // Without this the tab was one long stack where each sub-topic appeared
    // twice — once under Review drafts, again under Approved — so "what have I
    // got for the Calvin cycle?" meant scrolling past everything else.
    const [qFilter, setQFilter] = useState("all");

    useEffect(() => {
        if (!cover) { setCoverPreview(null); return; }
        const url = URL.createObjectURL(cover);
        setCoverPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [cover]);

    // What's actually stored, in the same shape the form holds — so the two can
    // be compared. The Details form gave no sign whether your edits had been
    // saved: the button looked identical before and after, and switching tab
    // silently carried unsaved changes with you.
    const savedSettings = useMemo(() => (lesson ? {
        title: lesson.title || "",
        subject: lesson.subject || "",
        class: lesson.class || "",
        topic: lesson.topic || "",
        type: lesson.type || "lesson_notes",
        description: lesson.description || "",
        body: lesson.body || "",
    } : null), [lesson]);

    const isDirty = !!cover || (!!settings && !!savedSettings
        && Object.keys(savedSettings).some((k) => settings[k] !== savedSettings[k]));

    const fetchAll = async () => {
        setLoading(true);
        try {
            const lRes = await authFetch(`/api/lessons/mine/${id}`);
            const data = await lRes.json().catch(() => ({}));
            if (!lRes.ok) throw new Error(data.message || "Failed to load content");
            const l = data.data.lesson;
            setLesson(l);
            setSegments(data.data.segments || []);
            setQuestions(data.data.questions || []);
            setFlashcards(data.data.flashcards || []);
            setSettings({
                title: l.title || "",
                subject: l.subject || "",
                class: l.class || "",
                topic: l.topic || "",
                type: l.type || "lesson_notes",
                description: l.description || "",
                body: l.body || "",
            });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, [id]);

    // Keep every pending draft ticked by default; the teacher unticks the ones
    // they don't want before hitting "Keep selected".
    useEffect(() => {
        const drafts = questions.filter((q) => q.review_status === "draft");
        setSelectedQ(new Set(drafts.map((q) => q.id)));
    }, [questions]);

    // Returns whether it saved, so "Save & continue" only moves on once the
    // details are actually stored — advancing past a failed save would leave the
    // teacher building sub-topics onto a lesson that never took their edits.
    const saveSettings = async () => {
        if (!settings.title.trim()) { toast.error("Give the lesson a title"); return false; }
        if (!settings.subject) { toast.error("Choose a subject"); return false; }
        if (!settings.class) { toast.error("Choose a class"); return false; }
        // Topic is required: it files this lesson's questions into the shared
        // bank, and students filter practice by it.
        if (!settings.topic.trim()) { toast.error("Choose a topic — pick one or type your own"); return false; }

        setSavingSettings(true);
        try {
            const body = new FormData();
            Object.entries(settings).forEach(([k, v]) => body.append(k, v ?? ""));
            if (cover) body.append("thumbnail", cover);
            const res = await authFetch(`/api/lessons/${id}`, { method: "PATCH", body });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't save");
            toast.success("Lesson details saved");
            setCover(null);
            fetchAll();
            return true;
        } catch (err) {
            toast.error(err.message);
            return false;
        } finally {
            setSavingSettings(false);
        }
    };

    // Generate for every readable sub-topic, or just one when `segmentIds` is
    // given (the per-row "Generate" button).
    const handleGenerate = async (segmentIds = null) => {
        const scopeIds = segmentIds && segmentIds.length ? segmentIds : null;
        const affected = scopeIds
            ? questions.filter((q) => q.review_status === "draft" && scopeIds.includes(q.segment_id))
            : questions.filter((q) => q.review_status === "draft");
        if (affected.length) {
            const ok = await confirm({
                title: "Replace the pending drafts?",
                message: `${affected.length} draft question${affected.length === 1 ? "" : "s"} you haven't kept yet will be replaced. Anything you already kept stays.`,
                confirmLabel: "Generate again",
            });
            if (!ok) return;
        }

        setGenerating(true);
        try {
            const res = await authFetch(`/api/lessons/${id}/generate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(scopeIds ? { ...genOptions, segmentIds: scopeIds } : genOptions),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                // The API now sends a message written for a teacher ("The AI is
                // busy right now…") plus whether trying again is likely to help,
                // so offer the retry rather than making them find the button.
                const err = new Error(data.message || "The AI couldn't be reached just now. Please try again in a moment.");
                err.retryable = data.retryable !== false;
                throw err;
            }

            toast.success(data.message || "Draft questions generated");
            // The API reports each sub-topic it couldn't read (a video, a scanned
            // PDF, an empty one) — surface that instead of leaving the teacher to
            // wonder why a sub-topic produced nothing.
            (data.data?.skipped || []).forEach((s) =>
                toast.warning(`Skipped "${s.title}" — ${s.reason}`, { duration: 7000 })
            );
            // Sub-topics that DID produce something, with a caveat worth knowing
            // (fewer than asked for, or a file read in samples).
            (data.data?.notes || []).forEach((n) =>
                toast.info(`"${n.title}" — ${n.reason}`, { duration: 8000 })
            );
            // Land on exactly what was just written: one sub-topic's batch when
            // that's what was asked for, otherwise the whole lesson.
            setQFilter(scopeIds && scopeIds.length === 1 ? String(scopeIds[0]) : "all");
            setTab("questions");
            fetchAll();
        } catch (err) {
            toast.error(err.message, {
                duration: 8000,
                ...(err.retryable ? { action: { label: "Try again", onClick: () => handleGenerate(segmentIds) } } : {}),
            });
        } finally {
            setGenerating(false);
        }
    };

    const toggleQ = (qid) => setSelectedQ((prev) => {
        const next = new Set(prev);
        if (next.has(qid)) next.delete(qid); else next.add(qid);
        return next;
    });
    const setAllQ = (ids, on) => setSelectedQ((prev) => {
        const next = new Set(prev);
        ids.forEach((qid) => (on ? next.add(qid) : next.delete(qid)));
        return next;
    });

    // Keep the ticked questions (bulk approve); the unticked ones stay as drafts
    // until you discard them.
    const approveSelected = async (draftIds) => {
        const ids = draftIds.filter((qid) => selectedQ.has(qid));
        if (!ids.length) { toast.error("Tick at least one question to keep"); return; }
        setReviewBusy(true);
        try {
            await Promise.all(ids.map((qid) => authFetch(`/api/teacher-questions/${qid}/approve`, { method: "PATCH" })));
            toast.success(`Kept ${ids.length} question${ids.length === 1 ? "" : "s"}`);
            fetchAll();
        } catch {
            toast.error("Couldn't approve some questions");
        } finally {
            setReviewBusy(false);
        }
    };

    // Drop the unticked (extra) questions in one go.
    const discardUnselected = async (draftIds) => {
        const ids = draftIds.filter((qid) => !selectedQ.has(qid));
        if (!ids.length) { toast.error("Nothing unticked to discard"); return; }
        const ok = await confirm({
            title: `Discard ${ids.length} question${ids.length === 1 ? "" : "s"}?`,
            message: "The unticked drafts will be deleted.",
            confirmLabel: "Discard",
            danger: true,
        });
        if (!ok) return;
        setReviewBusy(true);
        try {
            await Promise.all(ids.map((qid) => authFetch(`/api/teacher-questions/${qid}`, { method: "DELETE" })));
            toast.success("Removed the unticked questions");
            fetchAll();
        } catch {
            toast.error("Couldn't discard some questions");
        } finally {
            setReviewBusy(false);
        }
    };

    const approveFlashcard = async (fid) => { await authFetch(`/api/lessons/flashcards/${fid}/approve`, { method: "PATCH" }); fetchAll(); };
    const discardFlashcard = async (fid) => { await authFetch(`/api/lessons/flashcards/${fid}/discard`, { method: "PATCH" }); fetchAll(); };

    // Add a question BY HAND (the only path for video lessons, where AI is off).
    // The lesson supplies subject/class server-side; `segment_id` files it under
    // a sub-topic so it sits with the AI ones.
    const addQuestion = async (payload) => {
        const res = await authFetch(`/api/lessons/${id}/questions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Couldn't add the question");
        toast.success("Question added");
        fetchAll();
    };

    // Edit a question that's already there — an AI draft with one wrong option,
    // or an approved one that needs a fix. Before this the only way out was to
    // delete it and write it again.
    const saveQuestion = async (qid, form) => {
        const res = await authFetch(`/api/teacher-questions/${qid}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Couldn't save the question");
        toast.success("Question updated");
        setEditingQId(null);
        fetchAll();
    };

    const deleteQuestion = async (q) => {
        const ok = await confirm({
            title: "Delete this question?",
            message: "It's removed from your question bank for good.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        const res = await authFetch(`/api/teacher-questions/${q.id}`, { method: "DELETE" });
        if (!res.ok) { toast.error("Couldn't delete the question"); return; }
        toast.success("Question deleted");
        fetchAll();
    };

    const handlePublish = async () => {
        try {
            const res = await authFetch(`/api/lessons/${id}/publish`, { method: "POST" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Failed to publish");
            toast.success("Published — your students can see it now");
            fetchAll();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const loadStudents = async () => {
        try {
            const res = await authFetch(`/api/lessons/${id}/students`);
            const data = await res.json().catch(() => ({}));
            setStudents(data.data || []);
        } catch (err) {
            toast.error("Could not load student progress");
        }
    };

    useEffect(() => { if (tab === "students" && students === null) loadStudents(); }, [tab]);

    // ---- Sub-topics ----
    const addSegment = async ({ title, body, topic, file }) => {
        const fd = new FormData();
        fd.append("title", title);
        fd.append("body", body || "");
        fd.append("topic", topic || "");
        if (file) fd.append("attachment", file);
        const res = await authFetch(`/api/lessons/${id}/segments`, { method: "POST", body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Couldn't add sub-topic");
        toast.success("Sub-topic added");
        fetchAll();
    };
    const saveSegment = async (sid, { title, body, topic, file, removeAttachment }) => {
        const fd = new FormData();
        fd.append("title", title);
        fd.append("body", body || "");
        fd.append("topic", topic || "");
        if (file) fd.append("attachment", file);
        if (removeAttachment) fd.append("remove_attachment", "true");
        const res = await authFetch(`/api/lessons/segments/${sid}`, { method: "PATCH", body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Couldn't save sub-topic");
        toast.success("Sub-topic saved");
        fetchAll();
    };
    const deleteSegment = async (seg) => {
        const ok = await confirm({
            title: `Delete "${seg.title}"?`,
            message: "Its file and any unkept draft questions go with it. Questions you already kept stay in your bank.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        const res = await authFetch(`/api/lessons/segments/${seg.id}`, { method: "DELETE" });
        if (!res.ok) { toast.error("Couldn't delete sub-topic"); return; }
        toast.success("Sub-topic deleted");
        fetchAll();
    };
    const moveSegment = async (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= segments.length) return;
        const order = segments.map((s) => s.id);
        [order[index], order[target]] = [order[target], order[index]];
        const res = await authFetch(`/api/lessons/${id}/segments/reorder`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ order }),
        });
        if (!res.ok) { toast.error("Couldn't reorder"); return; }
        fetchAll();
    };

    // Upload one or more files at once — each becomes its own sub-topic (title =
    // the file name). The fast path for teachers who already have their lessons
    // as PDFs, Word docs or recorded clips instead of typing them out.
    const uploadDocuments = async (fileList, isVideoLesson) => {
        const chosen = Array.from(fileList || []);
        if (!chosen.length) return;
        const files = chosen.filter((f) => acceptSegmentFile(f, isVideoLesson));
        if (!files.length) return;

        setUploadingDocs(true);
        let ok = 0;
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("title", file.name.replace(/\.[^.]+$/, "") || "Document");
                fd.append("attachment", file);
                const res = await authFetch(`/api/lessons/${id}/segments`, { method: "POST", body: fd });
                if (res.ok) ok += 1;
            }
            if (ok) toast.success(`Added ${ok} file${ok === 1 ? "" : "s"} as sub-topic${ok === 1 ? "" : "s"}`);
            if (ok < files.length) toast.error(`${files.length - ok} file(s) couldn't be uploaded — check the type/size.`);
            fetchAll();
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploadingDocs(false);
        }
    };

    // Jump from a sub-topic straight to its questions, rather than switching tab
    // and hunting for its group.
    const reviewSegment = (sid) => { setQFilter(String(sid)); setTab("questions"); };

    const draftQuestions = useMemo(
        () => questions.filter((q) => q.review_status === "draft" && (qFilter === "all" || String(q.segment_id ?? "") === qFilter)),
        [questions, qFilter]
    );
    const approvedQuestions = useMemo(
        () => questions.filter((q) => q.review_status === "approved" && (qFilter === "all" || String(q.segment_id ?? "") === qFilter)),
        [questions, qFilter]
    );
    const draftFlashcards = useMemo(
        () => flashcards.filter((f) => f.status === "draft" && (qFilter === "all" || String(f.segment_id ?? "") === qFilter)),
        [flashcards, qFilter]
    );
    // Per-sub-topic tallies drive both the filter pills and each sub-topic row's
    // "6 pending review" strip.
    const questionStats = useMemo(() => {
        const map = new Map();
        const bump = (key, field) => {
            const cur = map.get(key) || { draft: 0, approved: 0 };
            cur[field] += 1;
            map.set(key, cur);
        };
        questions.forEach((q) => bump(String(q.segment_id ?? ""), q.review_status === "draft" ? "draft" : "approved"));
        return map;
    }, [questions]);
    const totalDrafts = questions.filter((q) => q.review_status === "draft").length;
    const totalApproved = questions.length - totalDrafts;

    if (loading) return <div className="p-10 text-gray-500">Loading…</div>;
    if (!lesson || !settings) return <div className="p-10 text-gray-500">Content not found.</div>;

    // Video lessons can't be summarised/quizzed by the AI (it can't watch the
    // clip), so those lessons are manual-questions-only.
    const isVideo = settings.type === "video";
    const generatable = segments.filter(isAiReadable);
    const counts = { subtopics: segments.length, questions: approvedQuestions.length + draftQuestions.length };

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className="min-h-screen bg-gray-50 font-montserrat">
                {ConfirmDialog}

                {/* Sticky header: identity + the one action that matters (publish),
                    with the tab bar attached so it's always reachable. */}
                <div className="sticky top-0 z-40 shadow-md">
                    <nav className="bg-[#2148C0] px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <button onClick={() => navigate("/teacher/lessons")} className="text-white shrink-0" title="Back to lessons">
                                <FaArrowLeft size={18} />
                            </button>
                            <div className="min-w-0">
                                <h1 className="text-base sm:text-lg font-bold text-white truncate leading-tight">{lesson.title}</h1>
                                <p className="text-[11px] text-blue-100 truncate">
                                    {lesson.subject}{lesson.class ? ` · ${lesson.class}` : ""}{lesson.topic ? ` · ${lesson.topic}` : ""}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <span className={`hidden sm:inline text-[11px] font-semibold px-2 py-1 rounded-full ${
                                lesson.status === "published" ? "bg-green-400/20 text-green-50" : "bg-white/15 text-blue-50"
                            }`}>
                                {lesson.status === "published" ? "Published" : "Draft"}
                            </span>
                            {/* Opens the real student reader against this lesson —
                                a draft included, since the owner can always read
                                their own. Nothing the teacher does in there is
                                recorded against them. */}
                            <button onClick={() => navigate(`/lessons/${id}?preview=1`)}
                                title="Open this lesson exactly as your students see it"
                                className="bg-white/15 hover:bg-white/25 text-white px-3 py-2 rounded-lg font-semibold flex items-center gap-2 text-sm">
                                <FaUserGraduate size={12} /> <span className="hidden sm:inline">Preview</span>
                            </button>
                            {lesson.status !== "published" && (
                                <button onClick={handlePublish} className="bg-white text-blue-700 px-3 sm:px-4 py-2 rounded-lg font-semibold flex items-center gap-2 text-sm">
                                    <FaUpload size={12} /> Publish
                                </button>
                            )}
                        </div>
                    </nav>

                    <div className="bg-white border-b border-gray-200 px-2 sm:px-6 flex gap-1 overflow-x-auto">
                        {TABS.map(({ id: tid, label, Icon }) => {
                            const active = tab === tid;
                            const badge = tid === "subtopics" ? counts.subtopics : tid === "questions" ? counts.questions : null;
                            return (
                                <button
                                    key={tid}
                                    onClick={() => setTab(tid)}
                                    className={`px-3 sm:px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition flex items-center gap-2 ${
                                        active
                                            ? "border-blue-600 text-blue-700"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    <Icon size={13} /> {label}
                                    {badge != null && (
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}>
                                            {badge}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="max-w-4xl mx-auto p-4 sm:p-6">
                    {/* ---------------- Details ---------------- */}
                    {tab === "details" && (
                        // Three groups rather than one flat run of eight controls:
                        // what the lesson IS, how it LOOKS to a student, and the
                        // optional intro. Everything used to carry identical weight
                        // in a single column, with the cover stranded at the bottom
                        // while the right half of the page sat empty.
                        <div className="space-y-4">
                            {needsSetup && (
                                <ProfileSetupNotice
                                    missingSubjects={!hasSubjects}
                                    missingClasses={!hasClasses}
                                    onSetup={() => navigate("/teacher/marketplace/profile")}
                                />
                            )}

                            <FormSection title="About this lesson" subtitle="How it's filed, and how students find it." Icon={FaCog}>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        Title <span className="text-red-500">*</span>
                                    </label>
                                    <input placeholder="e.g. Photosynthesis explained"
                                        className="border border-gray-200 rounded-xl p-2.5 w-full focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                                        value={settings.title} onChange={(e) => setSettings({ ...settings, title: e.target.value })} />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <SubjectPicker label="Subject" required allowCustom={false} subjects={subjects} value={settings.subject}
                                        onChange={(v) => setSettings({ ...settings, subject: v })} />
                                    <ClassPills label="Class" required classes={classes} value={settings.class}
                                        onChange={(v) => setSettings({ ...settings, class: v })}
                                        hint="Only the classes you teach are listed — add more under Marketplace → Profile." />
                                </div>

                                <TopicSelect label="Topic" required
                                    hint="Required — it's how these questions are filed in the bank and how students find them."
                                    subject={settings.subject} topicsBySubject={topicsBySubject}
                                    value={settings.topic} onChange={(v) => setSettings({ ...settings, topic: v })} />
                            </FormSection>

                            <FormSection title="How it looks to students" subtitle="The card they see before opening it." Icon={FaImage}>
                                <div className="grid grid-cols-1 lg:grid-cols-[13rem_1fr] gap-5">
                                    <div>
                                        <p className="text-sm font-medium text-gray-700 mb-1">Cover image</p>
                                        {coverPreview ? (
                                            <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 border"><img src={coverPreview} alt="cover" className="w-full h-full object-cover" /></div>
                                        ) : (
                                            <ContentThumb type={settings.type} thumbnailUrl={lesson.thumbnail_url} title={settings.title || "Your content"} />
                                        )}
                                        <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 cursor-pointer border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-300">
                                            <FaImage className="shrink-0" /> <span className="truncate">{cover ? cover.name : "Change cover (JPG/PNG)"}</span>
                                            <input type="file" accept=".jpg,.jpeg,.png" className="hidden" onChange={(e) => setCover(e.target.files[0])} />
                                        </label>
                                    </div>

                                    <div className="space-y-4 min-w-0">
                                        <TypePicker label="Content type" value={settings.type} onChange={(v) => setSettings({ ...settings, type: v })} />
                                        {/* The type isn't just a label — picking Video
                                            restricts sub-topics to clips and turns AI
                                            generation off. Say so here, where the choice
                                            is made, rather than letting it surface as a
                                            rejected upload two tabs later. */}
                                        {settings.type === "video" && (
                                            <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                                <FaVideo size={11} className="mt-0.5 shrink-0" />
                                                <span>
                                                    Sub-topics in a video lesson must be video files, and you'll write the
                                                    practice questions by hand — AI can't watch a clip.
                                                </span>
                                            </p>
                                        )}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-1 block">Short description</label>
                                            <textarea placeholder="One line shown on the lesson card" rows={2}
                                                className="border border-gray-200 rounded-xl p-2.5 w-full focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                                                value={settings.description} onChange={(e) => setSettings({ ...settings, description: e.target.value })} />
                                        </div>
                                    </div>
                                </div>
                            </FormSection>

                            <FormSection title="Intro" subtitle="Optional — the detail belongs in sub-topics." Icon={FaListUl}>
                                <textarea placeholder="A short overview of the whole lesson" rows={3}
                                    className="border border-gray-200 rounded-xl p-2.5 w-full focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                                    value={settings.body} onChange={(e) => setSettings({ ...settings, body: e.target.value })} />
                            </FormSection>

                            {/* Both actions are real buttons, grouped left — the same
                                filled + bordered pairing a sub-topic uses for Save /
                                Generate. The forward step also saves: as a bare link
                                it competed with Save, and taking it meant carrying
                                unsaved edits onto the next tab. */}
                            <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 flex items-center gap-2 flex-wrap">
                                <button onClick={saveSettings} disabled={savingSettings || !isDirty}
                                    className="bg-blue-600 hover:bg-blue-700 text-white border border-transparent px-5 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2">
                                    <FaSave size={13} /> {savingSettings ? "Saving…" : "Save details"}
                                </button>
                                <button
                                    onClick={async () => {
                                        // Nothing changed — don't fire a pointless PATCH.
                                        if (!isDirty) { setTab("subtopics"); return; }
                                        if (await saveSettings()) setTab("subtopics");
                                    }}
                                    disabled={savingSettings}
                                    className="border border-blue-200 text-blue-700 hover:bg-blue-50 px-5 py-2.5 rounded-xl font-semibold disabled:opacity-40 flex items-center gap-2">
                                    {isDirty ? "Save & add sub-topics" : "Add sub-topics"} <FaArrowRight size={11} />
                                </button>
                                <SaveState dirty={isDirty} saving={savingSettings} />
                            </div>

                            {/* What's still missing before students can see any of
                                this. The Publish button used to be the only way to
                                find out, and it answered with an error. */}
                            <PublishChecklist
                                lesson={lesson}
                                saved={savedSettings}
                                dirty={isDirty}
                                segmentCount={segments.length}
                                approvedCount={totalApproved}
                                draftCount={totalDrafts}
                                onPublish={handlePublish}
                                onGoToSubtopics={() => setTab("subtopics")}
                                onGoToQuestions={() => { setQFilter("all"); setTab("questions"); }}
                            />
                        </div>
                    )}

                    {/* ---------------- Sub-topics ---------------- */}
                    {tab === "subtopics" && (
                        <div className="space-y-4">
                            <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                            <FaLayerGroup className="text-blue-600" size={14} /> Sub-topics
                                        </h3>
                                        <p className="text-xs text-gray-500 mt-1 max-w-lg">
                                            {isVideo
                                                ? "Break the lesson into clips. Each video you upload becomes its own sub-topic — AI can't watch video, so you'll write the questions yourself."
                                                : "Break the lesson into parts. Type a note or drop in a PDF/Word file — each file becomes its own sub-topic, and the AI writes questions from what's inside it."}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <label className={`flex items-center gap-2 text-sm font-semibold cursor-pointer bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-2 rounded-lg ${uploadingDocs ? "opacity-50 pointer-events-none" : ""}`}>
                                            <FaFileUpload size={13} /> {uploadingDocs ? "Uploading…" : isVideo ? "Upload videos" : "Upload files"}
                                            <input type="file" accept={acceptFor(isVideo, BULK_ACCEPT)} multiple className="hidden"
                                                onChange={(e) => { uploadDocuments(e.target.files, isVideo); e.target.value = ""; }} />
                                        </label>
                                        <button onClick={() => setAddingSegment(true)}
                                            className="flex items-center gap-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg">
                                            <FaPlus size={12} /> Add
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {segments.length === 0 && !addingSegment && (
                                <div className="text-center py-14 bg-white rounded-2xl shadow-sm">
                                    <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto"><FaListUl size={20} /></div>
                                    <p className="mt-3 text-gray-700 font-medium">No sub-topics yet</p>
                                    <p className="text-sm text-gray-400 mt-1">Upload your lesson PDFs, or add a sub-topic and type it out.</p>
                                </div>
                            )}

                            <div className="space-y-2">
                                {segments.map((seg, idx) => (
                                    <SegmentRow
                                        key={seg.id}
                                        segment={seg}
                                        index={idx}
                                        total={segments.length}
                                        open={openSegments.has(seg.id)}
                                        onToggle={() => toggleSegmentOpen(seg.id)}
                                        stats={questionStats.get(String(seg.id)) || { draft: 0, approved: 0 }}
                                        subject={settings.subject}
                                        topicsBySubject={topicsBySubject}
                                        onSave={(patch) => saveSegment(seg.id, patch)}
                                        onDelete={() => deleteSegment(seg)}
                                        onMove={(dir) => moveSegment(idx, dir)}
                                        onGenerate={isVideo ? null : () => handleGenerate([seg.id])}
                                        onReview={() => reviewSegment(seg.id)}
                                        generating={generating}
                                        isVideoLesson={isVideo}
                                    />
                                ))}
                            </div>

                            {/* The new sub-topic is appended, so the form that creates
                                it sits where it will appear rather than at the top. */}
                            {addingSegment ? (
                                <AddSegment
                                    subject={settings.subject}
                                    topicsBySubject={topicsBySubject}
                                    defaultTopic={settings.topic}
                                    isVideoLesson={isVideo}
                                    onAdd={addSegment}
                                    onCancel={() => setAddingSegment(false)}
                                />
                            ) : segments.length > 0 && (
                                <button onClick={() => setAddingSegment(true)}
                                    className="w-full border-2 border-dashed border-gray-300 rounded-xl p-3 text-gray-600 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2 font-medium text-sm">
                                    <FaPlus size={11} /> Add another sub-topic
                                </button>
                            )}

                            {segments.length > 0 && (
                                <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 flex items-center gap-3 flex-wrap">
                                    <button onClick={() => { setQFilter("all"); setTab("questions"); }}
                                        className="border border-blue-200 text-blue-700 hover:bg-blue-50 px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2">
                                        Go to questions <FaArrowRight size={11} />
                                    </button>
                                    {!isVideo && (
                                        <p className="text-xs text-gray-400">
                                            {generatable.length} of {segments.length} can be read by AI.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ---------------- Questions ---------------- */}
                    {tab === "questions" && (
                        <div className="space-y-4">
                            {/* Scope the whole tab to one sub-topic. Its drafts and
                                its approved questions then sit together, instead of
                                the same sub-topic appearing twice on a page that
                                ran to several screens. */}
                            {segments.length > 0 && (
                                <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-4">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs font-semibold text-gray-500 shrink-0">Showing</span>
                                        <FilterPill
                                            active={qFilter === "all"} onClick={() => setQFilter("all")}
                                            label="Whole lesson" badge={totalDrafts || null}
                                        />
                                        {segments.map((s, i) => {
                                            const st = questionStats.get(String(s.id)) || { draft: 0, approved: 0 };
                                            return (
                                                <FilterPill
                                                    key={s.id}
                                                    active={qFilter === String(s.id)}
                                                    onClick={() => setQFilter(String(s.id))}
                                                    label={`${i + 1}. ${s.title}`}
                                                    badge={st.draft || null}
                                                    muted={st.draft + st.approved === 0}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {isVideo ? (
                                <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 flex items-start gap-3">
                                    <span className="w-9 h-9 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"><FaVideo size={15} /></span>
                                    <div className="text-sm">
                                        <p className="font-semibold text-rose-800">Video lesson — questions are added by hand</p>
                                        <p className="text-rose-700 mt-0.5">AI can't watch a video, so write the practice questions yourself below.</p>
                                    </div>
                                </div>
                            ) : (
                                // Once there are drafts waiting, reviewing them is the
                                // job — not generating more. The panel folds away to a
                                // summary line so the review sits at the top of the tab.
                                <GeneratePanel
                                    segments={segments}
                                    generatable={generatable}
                                    genOptions={genOptions}
                                    setGenOptions={setGenOptions}
                                    generating={generating}
                                    onGenerate={() => handleGenerate(null)}
                                    onGoToSubtopics={() => setTab("subtopics")}
                                    startCollapsed={draftQuestions.length > 0}
                                />
                            )}

                            {!isVideo && lesson.ai_summary && (
                                <details className="bg-blue-50 rounded-xl p-4 group">
                                    <summary className="font-semibold text-blue-800 cursor-pointer list-none flex items-center gap-2">
                                        <FaChevronRight className="group-open:rotate-90 transition" size={11} /> AI summary by sub-topic
                                    </summary>
                                    <p className="text-sm text-blue-900 whitespace-pre-wrap mt-2 leading-relaxed">{formatAiText(lesson.ai_summary)}</p>
                                </details>
                            )}

                            {draftQuestions.length > 0 && (
                                <DraftReview
                                    drafts={draftQuestions}
                                    segments={segments}
                                    selectedQ={selectedQ}
                                    toggleQ={toggleQ}
                                    setAllQ={setAllQ}
                                    busy={reviewBusy}
                                    onKeep={approveSelected}
                                    onDiscard={discardUnselected}
                                    editingQId={editingQId}
                                    onEdit={setEditingQId}
                                    onSaveEdit={saveQuestion}
                                />
                            )}

                            {draftFlashcards.length > 0 && (
                                <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
                                    <h3 className="font-semibold text-gray-800 mb-3">Pending flashcards ({draftFlashcards.length})</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {draftFlashcards.map((f) => (
                                            <div key={f.id} className="border border-gray-100 rounded-xl p-3">
                                                <p className="font-medium text-gray-800 text-sm">{f.front}</p>
                                                <p className="text-sm text-gray-500 mt-1">{f.back}</p>
                                                <div className="flex gap-2 mt-2">
                                                    <button onClick={() => approveFlashcard(f.id)} className="text-green-600 hover:bg-green-50 p-2 rounded-lg" title="Keep"><FaCheck size={12} /></button>
                                                    <button onClick={() => discardFlashcard(f.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" title="Discard"><FaTrash size={12} /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <AddQuestion
                                segments={segments}
                                onAdd={addQuestion}
                                defaultSegmentId={qFilter === "all" ? "" : qFilter}
                            />

                            <ApprovedList
                                questions={approvedQuestions}
                                segments={segments}
                                editingQId={editingQId}
                                onEdit={setEditingQId}
                                onSaveEdit={saveQuestion}
                                onDelete={deleteQuestion}
                            />
                        </div>
                    )}

                    {/* ---------------- Students ---------------- */}
                    {tab === "students" && (
                        <div className="bg-white rounded-2xl shadow-sm p-5">
                            <h3 className="font-semibold text-gray-800 mb-3">Student progress</h3>
                            {students === null && <p className="text-sm text-gray-500">Loading…</p>}
                            {students && students.length === 0 && <p className="text-sm text-gray-500">No student has opened this yet.</p>}
                            {students && students.map((s) => (
                                <div key={s.student_id} className="flex justify-between text-sm py-2 border-t border-gray-100 first:border-t-0">
                                    <span className="text-gray-700">{s.name}</span>
                                    <span className="text-gray-500">{s.completed_at ? `Completed · ${s.practice_score ?? "-"}%` : "In progress"}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </MathJaxContext>
    );
};

// A titled group of fields on the Details tab, so the form reads as three short
// questions instead of one long undifferentiated column.
const FormSection = ({ title, subtitle, Icon, children }) => (
    <section className="bg-white rounded-2xl shadow-sm p-5 sm:p-6">
        <div className="mb-4 flex items-start gap-3">
            {Icon && (
                <span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={14} />
                </span>
            )}
            <div className="min-w-0">
                <h2 className="font-semibold text-gray-800">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
            </div>
        </div>
        <div className="space-y-4">{children}</div>
    </section>
);

// Whether what's on screen matches what's stored. Without it, "did that save?"
// could only be answered by reloading the page.
const SaveState = ({ dirty, saving }) => {
    if (saving) return <span className="text-xs text-gray-400 ml-auto">Saving…</span>;
    return dirty ? (
        <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 ml-auto inline-flex items-center gap-1.5">
            <FaExclamationTriangle size={9} /> Unsaved changes
        </span>
    ) : (
        <span className="text-xs text-gray-400 ml-auto inline-flex items-center gap-1.5">
            <FaCheckCircle size={10} className="text-green-500" /> All changes saved
        </span>
    );
};

// The gate to students, spelled out. The API refuses to publish without a
// subject, class, topic and at least one sub-topic (see publishLesson), and a
// lesson with no approved questions gives students notes and no practice —
// which is worth knowing before you publish, not after.
const PublishChecklist = ({
    lesson, saved, dirty, segmentCount, approvedCount, draftCount,
    onPublish, onGoToSubtopics, onGoToQuestions,
}) => {
    const published = lesson.status === "published";
    // Checked against what's SAVED, not what's typed — publishing reads the row.
    const detailsDone = !!(saved && saved.title && saved.subject && saved.class && saved.topic);
    const hasContent = segmentCount > 0 || !!(saved && saved.body.trim());
    const blockers = [!detailsDone, !hasContent].filter(Boolean).length;

    const items = [
        {
            done: detailsDone,
            label: detailsDone
                ? "Title, subject, class and topic are set"
                : "Fill in the title, subject, class and topic above",
        },
        {
            done: hasContent,
            label: segmentCount
                ? `${segmentCount} sub-topic${segmentCount === 1 ? "" : "s"} added`
                : "Add at least one sub-topic",
            action: segmentCount ? null : { label: "Add sub-topics", onClick: onGoToSubtopics },
        },
        {
            done: approvedCount > 0,
            optional: true,
            label: approvedCount > 0
                ? `${approvedCount} practice question${approvedCount === 1 ? "" : "s"} approved`
                : draftCount > 0
                    ? `${draftCount} question${draftCount === 1 ? "" : "s"} still waiting for your review`
                    : "No practice questions yet — students would get the notes only",
            action: (approvedCount > 0 && draftCount === 0) ? null : { label: draftCount ? "Review them" : "Add questions", onClick: onGoToQuestions },
        },
    ];

    return (
        <section className="bg-white rounded-2xl shadow-sm p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                    published ? "bg-green-50 text-green-600" : "bg-blue-50 text-blue-600"
                }`}>
                    {published ? <FaCheckCircle size={14} /> : <FaUpload size={13} />}
                </span>
                <div className="min-w-0">
                    <h2 className="font-semibold text-gray-800">
                        {published ? "Published to your students" : "Before you publish"}
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                        {published
                            ? "Any change you save here is live straight away."
                            : blockers === 0
                                ? "Everything needed is in place."
                                : `${blockers} thing${blockers === 1 ? "" : "s"} left to sort out.`}
                    </p>
                </div>
            </div>

            <ul className="space-y-2">
                {items.map((item) => (
                    <li key={item.label} className="flex items-start gap-2.5 text-sm">
                        {item.done
                            ? <FaCheckCircle size={13} className="text-green-500 mt-0.5 shrink-0" />
                            : item.optional
                                ? <FaRegCircle size={13} className="text-amber-400 mt-0.5 shrink-0" />
                                : <FaRegCircle size={13} className="text-gray-300 mt-0.5 shrink-0" />}
                        <span className={item.done ? "text-gray-600" : "text-gray-800"}>{item.label}</span>
                        {item.action && (
                            <button onClick={item.action.onClick} className="text-blue-600 font-semibold text-xs hover:underline shrink-0 ml-auto">
                                {item.action.label}
                            </button>
                        )}
                    </li>
                ))}
            </ul>

            {!published && (
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-3 flex-wrap">
                    <button onClick={onPublish} disabled={blockers > 0 || dirty}
                        title={dirty ? "Save your changes first" : blockers > 0 ? "Finish the list above first" : undefined}
                        className="bg-blue-600 hover:bg-blue-700 text-white border border-transparent px-5 py-2.5 rounded-xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                        <FaUpload size={12} /> Publish
                    </button>
                    {dirty && <p className="text-xs text-amber-700">Save your changes first.</p>}
                </div>
            )}
        </section>
    );
};

// One choice in the Questions tab's sub-topic scope bar. The badge is the count
// still awaiting review, which is the only number worth interrupting for.
const FilterPill = ({ active, onClick, label, badge, muted }) => (
    <button onClick={onClick}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1.5 max-w-[15rem] ${
            active
                ? "bg-blue-600 text-white border-blue-600"
                : muted
                    ? "bg-white text-gray-400 border-gray-200 hover:border-blue-300"
                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
        }`}>
        <span className="truncate">{label}</span>
        {badge ? (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                active ? "bg-white/25 text-white" : "bg-amber-100 text-amber-800"
            }`}>
                {badge}
            </span>
        ) : null}
    </button>
);

// ---------------------------------------------------------------------------
// One sub-topic, collapsed to a single row until you open it. Collapsed it
// still shows position, title, what file is attached, its topic and how many
// questions came from it — which is what a teacher scans for.
const SegmentRow = ({
    segment, index, total, open, onToggle, stats, subject, topicsBySubject,
    onSave, onDelete, onMove, onGenerate, onReview, generating, isVideoLesson,
}) => {
    const questionCount = stats.draft + stats.approved;
    const [title, setTitle] = useState(segment.title || "");
    const [body, setBody] = useState(segment.body || "");
    const [topic, setTopic] = useState(segment.topic || "");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(false);

    // A refetch (after save/reorder) hands back a new segment object — pull the
    // fields back in so the row doesn't keep showing pre-save values.
    useEffect(() => {
        setTitle(segment.title || "");
        setBody(segment.body || "");
        setTopic(segment.topic || "");
    }, [segment.id, segment.title, segment.body, segment.topic]);

    const meta = segment.hasAttachment ? attachmentMeta(segment.attachment_kind) : null;
    const readable = isAiReadable(segment);

    const save = async (extra = {}) => {
        if (!title.trim()) { toast.error("Sub-topic title is required"); return; }
        setSaving(true);
        try {
            await onSave({ title, body, topic, file, ...extra });
            setFile(null);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`bg-white rounded-xl shadow-sm border transition ${open ? "border-blue-300" : "border-transparent"}`}>
            {/* Collapsed header row */}
            <div className="flex items-center gap-2 p-3">
                <button onClick={onToggle} className="flex items-center gap-2.5 min-w-0 flex-1 text-left">
                    <span className="text-gray-300 shrink-0">
                        {open ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
                    </span>
                    <span className="w-6 h-6 rounded-md bg-gray-100 text-gray-500 text-[11px] font-bold flex items-center justify-center shrink-0">
                        {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block font-medium text-gray-800 truncate">{segment.title}</span>
                        <span className="flex items-center gap-1.5 flex-wrap mt-0.5">
                            {meta && (
                                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.chip}`}>
                                    <meta.Icon size={9} /> {meta.label}
                                </span>
                            )}
                            {segment.topic && (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{segment.topic}</span>
                            )}
                            <span className="text-[10px] text-gray-400 inline-flex items-center gap-1">
                                {questionCount > 0
                                    ? <><FaCheckCircle size={8} className="text-green-500" /> {questionCount} question{questionCount === 1 ? "" : "s"}</>
                                    : <><FaRegCircle size={8} /> no questions yet</>}
                            </span>
                            {stats.draft > 0 && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                    {stats.draft} to review
                                </span>
                            )}
                            {!readable && (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">AI can't read this</span>
                            )}
                        </span>
                    </span>
                </button>

                <div className="flex items-center gap-0.5 shrink-0">
                    <button onClick={() => onMove(-1)} disabled={index === 0} className="text-gray-300 hover:text-blue-600 p-1.5 disabled:opacity-30" title="Move up"><FaArrowUp size={12} /></button>
                    <button onClick={() => onMove(1)} disabled={index === total - 1} className="text-gray-300 hover:text-blue-600 p-1.5 disabled:opacity-30" title="Move down"><FaArrowDown size={12} /></button>
                    <button onClick={onDelete} className="text-gray-300 hover:text-red-500 p-1.5" title="Delete"><FaTrash size={12} /></button>
                </div>
            </div>

            {/* Expanded editor */}
            {open && (
                <div className="px-3 pb-4 pt-1 space-y-3 border-t border-gray-100">
                    <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Title</label>
                        <input placeholder="e.g. Light-dependent reactions"
                            className="border border-gray-200 rounded-lg p-2 w-full text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                            value={title} onChange={(e) => setTitle(e.target.value)} />
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">
                            Notes <span className="text-gray-400 font-normal">(optional if you attached a document)</span>
                        </label>
                        <textarea placeholder="Type the sub-topic here, or leave blank and attach a file below" rows={4}
                            className="border border-gray-200 rounded-lg p-2 w-full text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                            value={body} onChange={(e) => setBody(e.target.value)} />
                    </div>

                    <TopicSelect compact subject={subject} topicsBySubject={topicsBySubject} value={topic} onChange={setTopic}
                        hint="Questions from these notes are filed under this topic — changing it re-tags the ones already generated."
                        placeholder="Sub-topic's own topic (blank inherits the lesson's)" />

                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <label className="flex items-center gap-2 text-gray-600 cursor-pointer border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-300">
                            <FaPaperclip className="shrink-0" size={12} />
                            <span className="truncate max-w-[12rem] text-xs">{file ? file.name : segment.hasAttachment ? (isVideoLesson ? "Replace video" : "Replace file") : (isVideoLesson ? "Attach video" : "Attach file")}</span>
                            <input type="file" accept={acceptFor(isVideoLesson, SEGMENT_ACCEPT)} className="hidden"
                                onChange={(e) => { const f = acceptSegmentFile(e.target.files[0], isVideoLesson); setFile(f); if (!f) e.target.value = ""; }} />
                        </label>
                        {segment.hasAttachment && !file && (
                            <>
                                <button onClick={() => setPreview((p) => !p)} className="text-blue-600 hover:underline text-xs font-medium">
                                    {preview ? "Hide preview" : "Preview"}
                                </button>
                                <button onClick={() => save({ removeAttachment: true })} className="text-red-500 text-xs hover:underline font-medium">Remove file</button>
                            </>
                        )}
                    </div>

                    {preview && segment.media_url && (
                        <SecureMedia url={segment.media_url} kind={segment.attachment_kind}
                            documentUrl={segment.document_url} title={segment.title} />
                    )}

                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
                        <button onClick={() => save()} disabled={saving}
                            className="bg-blue-600 hover:bg-blue-700 text-white border border-transparent px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                            <FaSave size={12} /> {saving ? "Saving…" : "Save"}
                        </button>
                        {onGenerate && (
                            <button onClick={onGenerate} disabled={generating || !readable}
                                title={readable ? "Write questions from just this sub-topic" : "Add notes or a PDF/Word file first"}
                                className="border border-blue-200 text-blue-700 hover:bg-blue-50 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-40">
                                <FaMagic size={12} /> {generating ? "Generating…" : questionCount ? "Generate again" : "Generate questions"}
                            </button>
                        )}
                        {/* The questions made from these notes live one tab away.
                            Without this the only route to them was to switch tab
                            and find this sub-topic's group by eye. */}
                        {questionCount > 0 && (
                            <button onClick={onReview}
                                className="text-blue-700 hover:bg-blue-50 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ml-auto">
                                <FaQuestionCircle size={12} />
                                {stats.draft > 0 ? `Review ${stats.draft} pending` : `See ${questionCount} question${questionCount === 1 ? "" : "s"}`}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Inline "add a sub-topic" card. The topic starts on the lesson's so the
// sub-topic is never untagged — every question written from its notes is filed
// under whatever is in this box, and a teacher who wants a narrower tag can
// change it here rather than discovering the inherited one after generating.
const AddSegment = ({ subject, topicsBySubject, defaultTopic = "", isVideoLesson, onAdd, onCancel }) => {
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [topic, setTopic] = useState(defaultTopic);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!title.trim()) { toast.error("Sub-topic title is required"); return; }
        setSaving(true);
        try {
            await onAdd({ title, body, topic, file });
            setTitle(""); setBody(""); setTopic(defaultTopic); setFile(null);
            onCancel();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3 border border-blue-200">
            <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-800 text-sm">New sub-topic</h4>
                <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><FaTimes size={13} /></button>
            </div>
            <input autoFocus placeholder="Sub-topic title" className="border border-gray-200 rounded-lg p-2 w-full text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                value={title} onChange={(e) => setTitle(e.target.value)} />
            <textarea placeholder="Notes (optional if you attach a file)" rows={3}
                className="border border-gray-200 rounded-lg p-2 w-full text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                value={body} onChange={(e) => setBody(e.target.value)} />
            <TopicSelect subject={subject} topicsBySubject={topicsBySubject} value={topic} onChange={setTopic}
                hint="Questions written from this sub-topic's notes are filed under this topic."
                placeholder="Its own topic" />
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer border border-gray-200 rounded-lg px-3 py-2 w-fit hover:border-blue-300">
                <FaPaperclip className="shrink-0" size={12} /> <span className="truncate max-w-[12rem] text-xs">{file ? file.name : isVideoLesson ? "Attach the video" : "Attach a file (optional)"}</span>
                <input type="file" accept={acceptFor(isVideoLesson, SEGMENT_ACCEPT)} className="hidden"
                    onChange={(e) => { const f = acceptSegmentFile(e.target.files[0], isVideoLesson); setFile(f); if (!f) e.target.value = ""; }} />
            </label>
            <div className="flex items-center gap-3">
                <button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold text-sm disabled:opacity-50">
                    {saving ? "Adding…" : "Add sub-topic"}
                </button>
                <button onClick={onCancel} className="text-gray-500 text-sm font-semibold">Cancel</button>
            </div>
        </div>
    );
};

// The AI panel. Counts are PER SUB-TOPIC, so the panel shows the resulting
// total up front — a teacher asking for 5 across 6 sub-topics is getting 30
// questions and should know that before pressing the button.
const GeneratePanel = ({ segments, generatable, genOptions, setGenOptions, generating, onGenerate, onGoToSubtopics, startCollapsed = false }) => {
    const n = generatable.length;
    const num = (key) => Math.max(parseInt(genOptions[key], 10) || 0, 0);
    const totalQ = (num("mcqCount") + num("theoryCount")) * n;
    // Everything asked of the model for ONE sub-topic — that's what a single
    // response has to hold, so it's what determines whether it fits.
    const perSubtopic = num("mcqCount") + num("theoryCount") + num("flashcardCount");
    const [collapsed, setCollapsed] = useState(startCollapsed);

    if (!segments.length) {
        return (
            <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto"><FaMagic size={17} /></div>
                <p className="mt-3 font-medium text-gray-700">Add a sub-topic first</p>
                <p className="text-sm text-gray-400 mt-1">The AI writes questions from each sub-topic's notes and uploaded files.</p>
                <button onClick={onGoToSubtopics} className="mt-3 text-blue-600 font-semibold text-sm hover:underline">Go to sub-topics →</button>
            </div>
        );
    }

    if (collapsed) {
        return (
            <button onClick={() => setCollapsed(false)}
                className="w-full bg-white rounded-2xl shadow-sm px-4 sm:px-5 py-3 flex items-center gap-2 text-left hover:bg-gray-50">
                <FaMagic className="text-blue-600 shrink-0" size={13} />
                <span className="font-semibold text-gray-800 text-sm">Generate with AI</span>
                <span className="text-xs text-gray-400 truncate">· {n} sub-topic{n === 1 ? "" : "s"} ready</span>
                <FaChevronDown className="text-gray-300 ml-auto shrink-0" size={11} />
            </button>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2"><FaMagic className="text-blue-600" size={14} /> Generate with AI</h3>
                <button onClick={() => setCollapsed(true)} className="text-gray-300 hover:text-gray-500 shrink-0" aria-label="Hide the generate panel">
                    <FaTimes size={13} />
                </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
                One batch <span className="font-semibold text-gray-700">per sub-topic</span>, written from that sub-topic's notes and
                the text inside its PDF/Word file — so the questions match what you actually taught.
            </p>

            {n === 0 ? (
                <div className="mt-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl p-3">
                    None of your {segments.length} sub-topic{segments.length === 1 ? "" : "s"} can be read yet — the AI needs typed notes
                    or a text-based PDF/Word file (it can't read videos, images or scanned pages).
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-3 gap-3 mt-4">
                        {[["mcqCount", "Multiple choice", 20], ["theoryCount", "Theory", 10], ["flashcardCount", "Flashcards", 20]].map(([key, label, max]) => (
                            <label key={key} className="text-xs font-medium text-gray-600">
                                {label}
                                <input type="number" min={0} max={max}
                                    className="border border-gray-200 rounded-lg p-2 w-full mt-1 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                                    value={genOptions[key]} onChange={(e) => setGenOptions({ ...genOptions, [key]: e.target.value })} />
                                <span className="block text-[10px] text-gray-400 mt-1">per sub-topic</span>
                            </label>
                        ))}
                    </div>

                    {/* A big ask is split into several requests behind the scenes,
                        and can still come back short. Say so before the button is
                        pressed rather than explaining it in a warning afterwards. */}
                    {perSubtopic > 25 && (
                        <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            That's {perSubtopic} items from one sub-topic. Large batches are written in several
                            passes and can come back with fewer than you asked for — smaller numbers are more reliable,
                            and you can always run it again.
                        </p>
                    )}

                    <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                        <button disabled={generating} onClick={onGenerate}
                            className="bg-blue-600 hover:bg-blue-700 text-white border border-transparent px-5 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2">
                            <FaMagic size={13} /> {generating ? "Generating…" : `Generate for ${n} sub-topic${n === 1 ? "" : "s"}`}
                        </button>
                        <p className="text-xs text-gray-500">
                            ≈ <span className="font-semibold text-gray-700">{totalQ}</span> questions
                            {n < segments.length && <span className="text-amber-600"> · {segments.length - n} sub-topic(s) will be skipped</span>}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

// Draft review, grouped by the sub-topic each question came from — the whole
// point of generating per sub-topic is that a teacher can judge a batch against
// the material it came from.
const DraftReview = ({
    drafts, segments, selectedQ, toggleQ, setAllQ, busy, onKeep, onDiscard,
    editingQId, onEdit, onSaveEdit,
}) => {
    const groups = useMemo(() => {
        const bySegment = new Map();
        // Keyed by segment id, not title — two sub-topics can legitimately share
        // a title (e.g. two uploads with the same file name).
        segments.forEach((s) => bySegment.set(s.id, { key: `s${s.id}`, title: s.title, items: [] }));
        // Questions from a since-deleted sub-topic, or from a lesson with none.
        const loose = { key: "loose", title: "Not linked to a sub-topic", items: [] };
        drafts.forEach((q) => {
            const bucket = bySegment.get(q.segment_id) || loose;
            bucket.items.push(q);
        });
        const out = [...bySegment.values()].filter((g) => g.items.length);
        if (loose.items.length) out.push(loose);
        return out;
    }, [drafts, segments]);

    const draftIds = drafts.map((q) => q.id);
    const selCount = draftIds.filter((qid) => selectedQ.has(qid)).length;
    const allOn = selCount === draftIds.length;

    return (
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="font-semibold text-gray-800">
                    Review drafts <span className="text-gray-400 font-normal">· {selCount} of {drafts.length} ticked</span>
                </h3>
                <button onClick={() => setAllQ(draftIds, !allOn)} className="text-xs font-semibold text-blue-600 hover:underline">
                    {allOn ? "Untick all" : "Tick all"}
                </button>
            </div>
            <p className="text-xs text-gray-500 mt-1 mb-3">Untick anything you don't want, then keep the rest.</p>

            <div className="space-y-4">
                {groups.map((group) => {
                    const ids = group.items.map((q) => q.id);
                    const on = ids.filter((qid) => selectedQ.has(qid)).length;
                    return (
                        <div key={group.key}>
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <h4 className="text-xs font-bold uppercase tracking-wide text-gray-400 truncate">
                                    {group.title} <span className="text-gray-300">· {on}/{ids.length}</span>
                                </h4>
                                <button onClick={() => setAllQ(ids, on !== ids.length)} className="text-[11px] font-semibold text-blue-600 hover:underline shrink-0">
                                    {on === ids.length ? "Untick" : "Tick"} all
                                </button>
                            </div>
                            <div className="space-y-2">
                                {group.items.map((q) => {
                                    if (q.id === editingQId) {
                                        return (
                                            <QuestionEditor key={q.id} question={q} segments={segments}
                                                onSave={(form) => onSaveEdit(q.id, form)} onCancel={() => onEdit(null)} />
                                        );
                                    }
                                    const checked = selectedQ.has(q.id);
                                    return (
                                        // The checkbox owns its own <label> so the Edit button beside it
                                        // doesn't tick the box on the way through.
                                        <div key={q.id} className={`flex items-start gap-2 rounded-xl p-3 border-2 transition ${
                                            checked ? "border-blue-300 bg-blue-50/30" : "border-gray-100 opacity-70"
                                        }`}>
                                            <label className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer">
                                                <input type="checkbox" checked={checked} onChange={() => toggleQ(q.id)} className="mt-1 h-4 w-4 accent-blue-600 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="text-[10px] tracking-wide font-bold uppercase text-gray-400">{QTYPE_LABELS[q.question_type] || q.question_type}</span>
                                                        {q.topic && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{q.topic}</span>}
                                                    </div>
                                                    <MathText text={q.question} className="block font-medium text-gray-800 text-sm" />
                                                    {q.question_type === "mcq" && (
                                                        <ul className="mt-1 text-sm text-gray-600">
                                                            {["A", "B", "C", "D"].map((l) => (
                                                                <li key={l} className={q.correctAnswer === l ? "text-green-600 font-semibold" : ""}>
                                                                    {l}. <MathText text={q[`option_${l.toLowerCase()}`]} />
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    {q.question_type === "theory" && q.explanation && (
                                                        <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">Model answer: {formatAiText(q.explanation)}</p>
                                                    )}
                                                </div>
                                            </label>
                                            <button onClick={() => onEdit(q.id)} title="Edit this question"
                                                className="text-gray-400 hover:text-blue-600 p-1.5 shrink-0">
                                                <FaPen size={12} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                <button onClick={() => onKeep(draftIds)} disabled={busy || selCount === 0}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50">
                    <FaCheck size={12} /> {busy ? "Saving…" : `Keep ticked (${selCount})`}
                </button>
                <button onClick={() => onDiscard(draftIds)} disabled={busy || allOn}
                    className="text-red-600 border border-red-200 hover:bg-red-50 px-4 py-2.5 rounded-xl font-semibold flex items-center gap-2 disabled:opacity-40">
                    <FaTrash size={12} /> Discard unticked
                </button>
            </div>
        </div>
    );
};

// Approved questions, also grouped by sub-topic, collapsed by default so the
// tab stays short once a lesson has plenty of them.
const ApprovedList = ({ questions, segments, editingQId, onEdit, onSaveEdit, onDelete }) => {
    const groups = useMemo(() => {
        const bySegment = new Map();
        segments.forEach((s) => bySegment.set(s.id, { key: `s${s.id}`, title: s.title, items: [] }));
        const loose = { key: "loose", title: "Not linked to a sub-topic", items: [] };
        questions.forEach((q) => (bySegment.get(q.segment_id) || loose).items.push(q));
        const out = [...bySegment.values()].filter((g) => g.items.length);
        if (loose.items.length) out.push(loose);
        return out;
    }, [questions, segments]);

    return (
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
            <h3 className="font-semibold text-gray-800 mb-1">Approved practice ({questions.length})</h3>
            {questions.length === 0 ? (
                <p className="text-sm text-gray-400">Nothing kept yet — generate or write questions above.</p>
            ) : (
                <div className="space-y-2 mt-3">
                    {groups.map((group) => (
                        // A single remaining group means the tab is scoped to one
                        // sub-topic — there's nothing to collapse away from, so open it.
                        <details key={group.key} open={groups.length === 1} className="border border-gray-100 rounded-xl group">
                            <summary className="px-3 py-2.5 cursor-pointer list-none flex items-center gap-2 text-sm font-medium text-gray-700">
                                <FaChevronRight className="group-open:rotate-90 transition text-gray-300" size={10} />
                                <span className="truncate flex-1">{group.title}</span>
                                <span className="text-[11px] text-gray-400 shrink-0">{group.items.length}</span>
                            </summary>
                            <div className="px-3 pb-3 space-y-1.5">
                                {group.items.map((q) => (
                                    q.id === editingQId ? (
                                        <div key={q.id} className="pt-2">
                                            <QuestionEditor question={q} segments={segments}
                                                onSave={(form) => onSaveEdit(q.id, form)} onCancel={() => onEdit(null)} />
                                        </div>
                                    ) : (
                                        <div key={q.id} className="border-t border-gray-100 pt-2 flex items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <MathText text={q.question} className="text-sm text-gray-700" />
                                                <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                                                    <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wide">{QTYPE_LABELS[q.question_type] || q.question_type}</span>
                                                    {q.topic && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{q.topic}</span>}
                                                </div>
                                            </div>
                                            <div className="flex items-center shrink-0">
                                                <button onClick={() => onEdit(q.id)} title="Edit this question" className="text-gray-400 hover:text-blue-600 p-1.5">
                                                    <FaPen size={12} />
                                                </button>
                                                <button onClick={() => onDelete(q)} title="Delete this question" className="text-gray-300 hover:text-red-500 p-1.5">
                                                    <FaTrash size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    )
                                ))}
                            </div>
                        </details>
                    ))}
                </div>
            )}
        </div>
    );
};

// Hand-write a practice question. MCQ needs four options + the correct letter;
// theory needs just the question + an optional model answer. This is the only
// way to add questions to a video lesson, where AI generation is off. The fields
// and validation are shared with the inline editor above (components/QuestionForm).
const AddQuestion = ({ segments, onAdd, defaultSegmentId = "" }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ ...EMPTY_QUESTION, segment_id: defaultSegmentId });
    const [saving, setSaving] = useState(false);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    // Writing a question while the tab is scoped to one sub-topic should file it
    // there by default — that's the sub-topic you're looking at.
    useEffect(() => {
        if (!open) setForm((f) => ({ ...f, segment_id: defaultSegmentId }));
    }, [defaultSegmentId, open]);

    const submit = async () => {
        const problem = validateQuestion(form);
        if (problem) { toast.error(problem); return; }
        setSaving(true);
        try {
            await onAdd(form);
            setForm({ ...EMPTY_QUESTION, segment_id: defaultSegmentId });
            setOpen(false);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} className="w-full border-2 border-dashed border-gray-300 rounded-xl p-4 text-gray-600 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2 font-medium">
                <FaPen size={12} /> Write a question by hand
            </button>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4 border border-blue-200">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2"><FaPen size={12} /> Write a question</h3>
                <button onClick={() => { setOpen(false); setForm({ ...EMPTY_QUESTION, segment_id: defaultSegmentId }); }} className="text-gray-400 hover:text-gray-600"><FaTimes size={13} /></button>
            </div>

            <QuestionFields form={form} set={set} segments={segments} />

            <button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2">
                <FaPlus size={12} /> {saving ? "Adding…" : "Add question"}
            </button>
        </div>
    );
};

export default ContentBuilder;
